import type { JobAttachment, JobTask } from '../api/jobWorkflow'

export function isChecklistImageFile(
  file: File,
  captureSource: 'camera' | 'upload' | string
): boolean {
  if (file.type.startsWith('image/')) return true
  // iOS camera / some PWAs hand back an empty MIME type.
  if (!file.type && captureSource === 'camera') return true
  if (!file.type && /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(file.name || '')) return true
  return false
}

export function isPendingAttachmentId(id: string): boolean {
  return String(id).startsWith('pending-')
}

/**
 * Prefer server photos, but keep local-only rows (pending blobs or just-uploaded)
 * for a task until the server returns at least one photo for that task.
 * This prevents a stale refetch from wiping the optimistic UI.
 */
export function mergeChecklistPhotos(
  serverPhotos: JobAttachment[],
  previous: JobAttachment[]
): JobAttachment[] {
  const serverIds = new Set(serverPhotos.map((s) => s.id))
  const serverTasksWithPhotos = new Set(
    serverPhotos.map((s) => s.job_task_id).filter(Boolean) as string[]
  )

  const extras = previous.filter((a) => {
    if (serverIds.has(a.id)) return false
    const taskId = a.job_task_id
    if (!taskId) return isPendingAttachmentId(a.id)
    if (serverTasksWithPhotos.has(taskId)) return false
    return true
  })

  return [...extras, ...serverPhotos]
}

/** Keep optimistic auto-check when the server briefly lags after toggle. */
export function mergeChecklistTasks(
  serverTasks: JobTask[],
  previous: JobTask[],
  opts?: { forceDoneTaskIds?: string[] }
): JobTask[] {
  const force = new Set(opts?.forceDoneTaskIds || [])
  const prevById = new Map(previous.map((t) => [t.id, t]))
  return serverTasks.map((t) => {
    if (force.has(t.id) && !t.is_done) return { ...t, is_done: true }
    const prev = prevById.get(t.id)
    // Preserve earlier optimistic checks — each photo load refetches all tasks.
    if (prev?.is_done && !t.is_done) return { ...t, is_done: true }
    return t
  })
}
